import React, { useEffect } from "react";
import { LEGAL, LegalBlock } from "./legal/content";

/**
 * Full legal document page (Privacy Policy / Terms / EULA).
 * Content is generated from the source PDFs in /docs by scripts/parse_legal.py
 * and bundled via src/legal/content.ts — rendered on-page (no PDF needed).
 */
export default function LegalPage({ id }: { id: string }) {
  const doc = LEGAL[id];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!doc) {
    return (
      <Shell title="Not found">
        <p className="text-frost-200/80">
          That document could not be found.{" "}
          <a href="#top" className="text-aurora-cyan hover:text-frost-50">
            Return home →
          </a>
        </p>
      </Shell>
    );
  }

  const rendered = buildRenderBlocks(doc.blocks);

  return (
    <Shell title={doc.title} org={doc.org} lastUpdated={doc.lastUpdated}>
      <div className="space-y-5">
        {rendered.map((r, i) => {
          if (r.kind === "list") return <BulletList key={i} items={r.items} />;
          if (r.kind === "sublist") return <SubList key={i} items={r.items} />;
          if (r.kind === "address")
            return <AddressBlock key={i} lines={r.lines} email={r.email} />;
          return <Block key={i} b={r.b} />;
        })}
      </div>
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */
/* Block normalisation — the PDF-derived content stores lettered sub-clauses   */
/* like "(a) …; (b) …" as flat paragraphs (sometimes several jammed into one,  */
/* or a lead-in "… if:" glued to the first item). We reshape those into an     */
/* indented sub-list, and turn the run-on contact address into stacked lines,  */
/* so the page matches the source document's layout.                           */
/* -------------------------------------------------------------------------- */

type RenderBlock =
  | { kind: "block"; b: LegalBlock }
  | { kind: "list"; items: string[] }
  | { kind: "sublist"; items: string[] }
  | { kind: "address"; lines: string[]; email?: string };

// A lettered marker "(a) " / "(ii) " that begins a sub-clause: only at the start
// of the text or right after a ":" / ";" boundary (optionally followed by an
// "or"/"and" connector). This keeps inline prose — "the (a) rights and (b) …",
// "breach of (a) …, (b) …" — and "link(s)" / "section 12(b)" from being mistaken
// for list markers, while still catching "…; or (d) …" endings.
const MARKER_BOUNDARY = /(^|[:;]\s(?:or\s|and\s)?)(\((?:[a-z]|[ivx]{1,4})\)\s)/g;

function splitLettered(s: string): { lead: string | null; subs: string[] } | null {
  const idxs: number[] = [];
  let m: RegExpExecArray | null;
  MARKER_BOUNDARY.lastIndex = 0;
  while ((m = MARKER_BOUNDARY.exec(s)) !== null) {
    idxs.push(m.index + m[1].length);
    if (MARKER_BOUNDARY.lastIndex === m.index) MARKER_BOUNDARY.lastIndex++;
  }
  if (idxs.length === 0) return null;
  const lead = idxs[0] > 0 ? s.slice(0, idxs[0]).trim() : null;
  const subs: string[] = [];
  for (let i = 0; i < idxs.length; i++) {
    const end = i + 1 < idxs.length ? idxs[i + 1] : s.length;
    subs.push(s.slice(idxs[i], end).trim());
  }
  return { lead: lead || null, subs };
}

function formatAddress(
  s: string,
): { lead: string | null; lines: string[]; email?: string } | null {
  if (!/Lavina\s*&\s*Tommy Inc\./.test(s)) return null;
  const start = s.indexOf("Winter Desktops");
  if (start < 0) return null;
  const lead = start > 0 ? s.slice(0, start).trim() : null;
  let body = s.slice(start);
  let email: string | undefined;
  const em = body.match(/([\w.+-]+@[\w-]+\.[\w.-]+)\s*$/);
  if (em) {
    email = em[1];
    body = body.slice(0, body.length - em[0].length).trim();
  }
  body = body
    .replace(/\s*(C\/-)/, "\n$1")
    .replace(/\s*(\d+\s+(?:N\s+)?Gould)/, "\n$1")
    .replace(/\s*(Sheridan)/, "\n$1")
    .replace(/\s*(United States)/, "\n$1");
  const lines = body
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  return { lead: lead || null, lines, email };
}

function buildRenderBlocks(blocks: LegalBlock[]): RenderBlock[] {
  const out: RenderBlock[] = [];
  const pushSubs = (subs: string[], freshGroup: boolean) => {
    const last = out[out.length - 1];
    if (!freshGroup && last && last.kind === "sublist") last.items.push(...subs);
    else out.push({ kind: "sublist", items: [...subs] });
  };
  for (const b of blocks) {
    if (b.t === "li") {
      const last = out[out.length - 1];
      if (last && last.kind === "list") last.items.push(b.s);
      else out.push({ kind: "list", items: [b.s] });
      continue;
    }
    if (b.t === "p") {
      const addr = formatAddress(b.s);
      if (addr) {
        if (addr.lead) out.push({ kind: "block", b: { t: "p", s: addr.lead } });
        out.push({ kind: "address", lines: addr.lines, email: addr.email });
        continue;
      }
      const lettered = splitLettered(b.s);
      if (lettered) {
        if (lettered.lead) {
          out.push({ kind: "block", b: { t: "p", s: lettered.lead } });
          pushSubs(lettered.subs, true);
        } else {
          pushSubs(lettered.subs, false);
        }
        continue;
      }
    }
    out.push({ kind: "block", b });
  }
  return out;
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-3.5 pl-2 sm:pl-3">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3 text-frost-200/85">
          <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-aurora-cyan/70" />
          <span className="leading-relaxed">{s}</span>
        </li>
      ))}
    </ul>
  );
}

function SubList({ items }: { items: string[] }) {
  return (
    <div className="space-y-3 pl-6 sm:pl-9">
      {items.map((s, i) => {
        const m = s.match(/^(\((?:[a-z]|[ivx]{1,4})\))\s*([\s\S]*)$/);
        return (
          <p key={i} className="flex gap-2.5 leading-relaxed text-frost-200/85">
            {m ? (
              <>
                <span className="flex-none font-medium text-aurora-cyan/80">{m[1]}</span>
                <span>{m[2]}</span>
              </>
            ) : (
              <span>{s}</span>
            )}
          </p>
        );
      })}
    </div>
  );
}

function AddressBlock({ lines, email }: { lines: string[]; email?: string }) {
  return (
    <div className="leading-relaxed text-frost-200/85">
      {lines.map((l, i) => (
        <div key={i} className={i === 0 ? "font-semibold text-frost-100" : undefined}>
          {l}
        </div>
      ))}
      {email && (
        <div className="mt-1">
          <a href={`mailto:${email}`} className="text-aurora-cyan hover:text-frost-50">
            {email}
          </a>
        </div>
      )}
    </div>
  );
}

function Block({ b }: { b: LegalBlock }) {
  if (b.t === "table") {
    return <LegalTable headers={b.headers} rows={b.rows} />;
  }
  if (b.t === "h2") {
    return (
      <h2 className="!mt-12 scroll-mt-28 font-display text-2xl font-semibold tracking-tight text-frost-50 first:!mt-0">
        {prettyHeading(b.s)}
      </h2>
    );
  }
  // emphasised lead-ins ("In Short:", "PLEASE READ ...")
  const emphasised =
    /^in short[:.]/i.test(b.s) || b.s === b.s.toUpperCase();
  return (
    <p
      className={`leading-relaxed ${
        emphasised
          ? "font-medium text-frost-100"
          : "text-frost-200/85"
      }`}
    >
      {b.s}
    </p>
  );
}

/** Renders a proper table (e.g. the CCPA categories grid) inside a legal doc. */
function LegalTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.02]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className={`border-b border-white/10 bg-white/[0.05] px-4 py-3 text-left font-semibold text-frost-100 ${
                  i === headers.length - 1 ? "text-center" : ""
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="align-top">
              {r.map((c, ci) => {
                const isLast = ci === r.length - 1;
                return (
                  <td
                    key={ci}
                    className={`border-b border-white/5 px-4 py-3 leading-relaxed ${
                      ci === 0
                        ? "font-medium text-frost-100 whitespace-nowrap sm:whitespace-normal"
                        : "text-frost-200/80"
                    } ${isLast ? "text-center font-semibold text-aurora-cyan" : ""}`}
                  >
                    {c}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Title-case an ALL-CAPS heading while keeping numbered ones intact. */
function prettyHeading(s: string): string {
  const m = s.match(/^(\d+\.)\s+(.*)$/);
  const num = m ? m[1] + " " : "";
  const body = m ? m[2] : s;
  if (body === body.toUpperCase()) {
    const tc = body
      .toLowerCase()
      .replace(/\b([a-z])/g, (c) => c.toUpperCase())
      .replace(
        /\b(And|Or|Of|To|The|We|Do|On|In|A|An|For|Your|Our|Us|With|Whom|When)\b/g,
        (w) => w.toLowerCase()
      );
    // keep first word capitalised
    return num + tc.charAt(0).toUpperCase() + tc.slice(1);
  }
  return num + body;
}

function Shell({
  title,
  org,
  lastUpdated,
  children,
}: {
  title: string;
  org?: string;
  lastUpdated?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative min-h-screen bg-night-fade font-sans text-frost-100">
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-midnight-900/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-frost-300/30 to-aurora-violet/20 ring-1 ring-white/15">
              <span className="text-lg" role="img" aria-label="panda">
                🐼
              </span>
            </span>
            <span className="font-display text-lg font-semibold tracking-tight text-frost-50">
              Winter Panda
            </span>
          </a>
          <a
            href="#top"
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-sm font-medium text-frost-100 transition-colors hover:bg-white/10"
          >
            ← Back to home
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20">
        <div className="mb-10 border-b border-white/10 pb-8">
          {org && (
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-aurora-cyan">
              {org}
            </p>
          )}
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-frost-50 sm:text-5xl">
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-3 text-sm text-frost-200/60">
              Last updated: {lastUpdated}
            </p>
          )}
        </div>

        <article className="text-[15px]">{children}</article>

        {/* legal nav between docs */}
        <nav className="mt-16 flex flex-wrap gap-3 border-t border-white/10 pt-8">
          <LegalLink id="privacy" label="Privacy Policy" />
          <LegalLink id="terms" label="Terms & Conditions" />
          <LegalLink id="eula" label="EULA" />
        </nav>
      </main>

      <footer className="border-t border-white/5 bg-white/[0.02]">
        <div className="mx-auto max-w-4xl px-5 py-8 text-sm text-frost-200/55 sm:px-8">
          © 2026 Winter Desktops. All rights reserved. ·{" "}
          <a
            href="mailto:privacy@winterdesktops.com"
            className="hover:text-frost-50"
          >
            privacy@winterdesktops.com
          </a>
        </div>
      </footer>
    </div>
  );
}

function LegalLink({ id, label }: { id: string; label: string }) {
  return (
    <a
      href={`#/legal/${id}`}
      className="rounded-full glass px-4 py-2 text-sm font-medium text-frost-200/80 transition-colors hover:bg-white/10 hover:text-frost-50"
    >
      {label}
    </a>
  );
}
